// The API's shapes as the screens use them: the real schemas (apps/api/openapi.json) plus, where a screen
// shows something the API does not return yet, an optional field named after what it will be. The fixtures
// fill those fields with the design's data; against the real API they are absent and the screens show «—»
// or derive what they can. Every such field is an API gap (listed in the web track's report and in
// apps/web/README.md, "API gaps").
import type { Schemas } from './client';

export type Me = Schemas['MeOut'];
export type Locale = Schemas['Locale'];
export type PlanRef = Schemas['PlanRef'];
export type Person = Schemas['PersonOut'];
export type Member = Schemas['MemberOut'];
export type Source = Schemas['SourceOut'];
export type Media = Schemas['MediaOut'];
export type Job = Schemas['JobOut'];
export type Run = Schemas['RunOut'];
export type RunSource = Schemas['RunSourceOut'];
export type Plan = Schemas['PlanOut'];
export type User = Schemas['UserOut'] & {
  /** GAP: the API has no «lead PM» flag («PM · lead» in Roles and access). */
  is_lead_pm?: boolean;
};
export type SubscriptionOut = Schemas['SubscriptionOut'];
export type SubscriptionRow = Schemas['SubscriptionRow'];
export type SubscriptionReport = Schemas['SubscriptionReport'];
export type SettingsOut = Schemas['SettingsOut'];

/** What the organizations list shows in «Статус»: the furthest state of its sites. */
export type OrgHeadline = 'production' | 'demo' | 'awaiting_dns' | 'draft' | 'build_error' | 'landing' | 'archived' | 'active' | 'trial';

type OrgExtras = {
  /** GAP: no per-organization headline status yet (the list derives one from `status`). */
  headline?: OrgHeadline;
};
export type OrganizationSummary = Schemas['OrganizationSummary'] & OrgExtras;
export type Organization = Schemas['OrganizationOut'] & OrgExtras;

export type Website = Schemas['WebsiteOut'] & {
  /** GAP (P4): the active variant's build failed («C · сбой»); `variant` then holds the key only. */
  variant_failed?: boolean;
};

/** One audit row. */
/** An audit row: the API names the actor, writes the summary and gives the outcome (the coloured dot). */
export type AuditEntry = Schemas['AuditOut'];

export type Dashboard = Schemas['Dashboard'] & {
  /** GAP: the demo zone shown under «Активные демо» (an Admin's view falls back to Settings → Домены). */
  demo_zone?: string | null;
};

/** A job as the event stream and the jobs list carry it (the SSE `job` summary is a subset of JobOut). */
export type JobSummary = Pick<Job, 'id' | 'type' | 'status' | 'step' | 'progress' | 'eta_seconds' | 'attempts' | 'target' | 'organization_id'> & {
  /** GAP: a display name for targets the screens cannot resolve (a domain, a store). */
  label?: string;
};

// ── Settings sections (apps/api/app/modules/settings/sections.py): `values` is a dict in the schema ──

export type SecretState = { set: boolean; masked: string | null };

/** The model routes a provider is chosen for (P8-A03; apps/api settings/sections.py AI_ROUTES). */
export const AI_ROUTES = ['plan_page', 'generate', 'self_review', 'chat_edit', 'research_extract', 'structure.suggest', 'preset', 'eval_judge'] as const;
export type AiRoute = (typeof AI_ROUTES)[number];
export type AiValues = {
  provider: 'anthropic';
  adapter: 'claude_api' | 'claude_code' | 'claude_api_and_code';
  model: string;
  effort: 'low' | 'medium' | 'high' | 'xhigh' | 'max';
  org_monthly_budget_usd: number;
  library_monthly_budget_usd: number;
  parallel_jobs: number;
  /** P8-A03: the second provider's model, and the routes it answers (absent = none; the rest are Claude). */
  openai_model?: string;
  route_providers?: Partial<Record<AiRoute, 'anthropic' | 'openai'>>;
};
export type McpGroup = { group: string; tools: string[]; mode: 'read' | 'isolated' | 'sandbox' | 'needs_confirmation' };
export type StorageValues = {
  object_storage: string;
  bucket_label: string;
  cdn: string;
  html_cache: 'revalidate' | 'short_ttl';
  assets_cache: 'immutable';
  endpoint_configured?: boolean;
  prefixes?: string[];
};
export type DomainValues = {
  registrar: 'none' | 'ahost' | 'suvan' | 'openprovider' | 'fake';
  registrar_api_user: string | null;
  pm_monthly_budget_uzs: number;
  wildcard_tls_until: string | null;
  demo_zone?: string | null;
  /** P7-A01: the registrar price table the quotes come from (UZS a year, with the date checked; the API fills the defaults). */
  prices: { registrar: 'ahost' | 'suvan' | 'openprovider' | 'fake'; zone: string; register_uzs: number; renew_uzs: number; redemption_uzs: number | null; as_of: string }[];
};
export type RulesValues = { demo_after_production: 'gone_410' | 'redirect_301'; demo_password_default: boolean };
export type FixedRule = { key: string; value: string; decision: string };

export type Section<V> = Omit<SettingsOut, 'values' | 'secrets' | 'fixed'> & {
  values: V;
  secrets: Record<string, SecretState>;
  fixed?: FixedRule[] | null;
};

// ── Research connectors (GET /admin/settings/connectors; apps/api/app/modules/connectors/service.py
// `describe()`): the schema types each one as a dict, so the shape is written here ──

export type ConnectorLimits = { requests_per_minute: number; max_documents: number; max_bytes: number; min_interval: number };
export type ConnectorOut = {
  kind: string;
  family: 'research' | 'catalog';
  version: string;
  enabled: boolean;
  limits: ConnectorLimits;
  /** write-only: only ever {set, masked} */
  credentials: Record<string, SecretState>;
  /** booleans and capabilities only (CONNECTORS §4); `configured` ignores optional credentials */
  health: { configured: boolean; capabilities: string[]; detail: string | null };
};
export type ConnectorPatch = Schemas['ConnectorPatch'];

// ── Design Library (P3-D01, P3-D02; library/router.py) ──

export type PresetOut = Schemas['PresetOut'];
export type PresetDetail = Schemas['PresetDetail'];
export type PresetVersion = Schemas['PresetVersionOut'];
export type PresetPage = Schemas['PresetPage'];
export type PresetStatus = Schemas['PresetStatus'];
export type PresetOrigin = Schemas['PresetOrigin'];
export type SectionTypeOut = Schemas['SectionTypeOut'];

// ── Catalog sources (P3-D03; GET/PATCH /admin/catalog-sources): `health` is a dict in the schema ──

export type LicenseStatus = Schemas['LicenseStatus'];
export type CatalogSyncCounts = { seen: number; created: number; updated: number; unchanged: number; skipped: number; failed: number };
export type CatalogSourceOut = Omit<Schemas['CatalogSourceOut'], 'health'> & {
  /** after a sync: {last_sync: counts, at} (catalog.py `sync_registry`) */
  health: {
    last_sync?: CatalogSyncCounts;
    at?: string;
    /** GAP: a failed sync writes no error into health yet (the row would show it) */
    error?: string;
  };
};
export type CatalogSourcePatch = Schemas['CatalogSourcePatch'];
export type SyncStarted = Schemas['SyncStarted'];

/** A number from the API's decimal strings ("4165000.00"). */
export function num(v: string | number | null | undefined): number {
  if (v === null || v === undefined || v === '') return 0;
  return typeof v === 'number' ? v : Number(v);
}
